import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Download, FileText, Loader2, Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/tooltip'
import { Input } from '@/components/ui/input'
import { Kbd } from '@/components/ui/misc'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { FiltersPanel } from './term-base/_components/filters-panel'
import { TermList } from './term-base/_components/term-list'
import { TermDetail } from './term-base/_components/term-detail'
import { TermCloud } from './term-base/_components/term-cloud'
import { ConceptBuildBar } from './term-base/_components/concept-build-bar'
import { ConceptsView } from './term-base/_components/concepts-view'
import {
  ConceptFiltersPanel,
  type ConceptSort,
} from './term-base/_components/concept-filters-panel'
import { DocumentsView } from './term-base/_components/documents-view'
import { CommandPalette } from './term-base/_components/command-palette'
import { useDebounced } from '@/hooks/use-debounced'
import { useTermBaseState, type Mode } from '@/hooks/use-term-base-state'
import { useQuery } from '@tanstack/react-query'
import { getCollections } from '@/lib/api/collections/get-collections'
import { getDocuments } from '@/lib/api/documents/get-documents'
import { useActiveCollection } from '@/hooks/use-active-collection'
import { CollectionPicker } from './term-base/_components/collection-picker'
import { CollapsiblePanel } from '@/components/ui/collapsible-panel'
import { SharePanel } from './term-base/_components/share-panel'
import {
  useDocumentTermIds,
  useDomains,
  useFilteredTerms,
  type SortKey,
  useConcepts,
} from '@/hooks/use-term-data'
import { apiErrorMessage, isBackendStarting, traceIdOf } from '@/lib/api-client'
import { cn, compareStrings } from '@/lib/utils'
import { TraceId } from '@/components/ui/trace-id'
import { exportCollectionTerms } from '@/lib/api/terms/export-terms'
import type { ConceptBuildEvent } from '@/lib/api/concepts/build-concepts'
import type { Term } from '@/types/terms'

/**
 * The URL keeps the Estonian mode names — they are part of every shared link —
 * so the display label is looked up rather than derived from the value.
 */
const MODE_LABEL_KEY: Record<Mode, 'documents' | 'terms' | 'concepts'> = {
  dokumendid: 'documents',
  terminid: 'terms',
  moisted: 'concepts',
}

export function TermBasePage() {
  const { t } = useTranslation()
  const { state, patch, selectTerm, clearFilters } = useTermBaseState()
  const [sort, setSort] = useState<SortKey>('freq')
  const [conceptSort, setConceptSort] = useState<ConceptSort>('terms')
  // Reported up by the list so the filter panel can show shown/total without
  // duplicating the filtering logic that produces it.
  const [shownConcepts, setShownConcepts] = useState(0)

  const [exporting, setExporting] = useState(false)
  // Concept-build animation state: groups that formed during the current run
  // (persisted concept_ids arrive only after the refetch) and the members
  // still in their arrival flash.
  const [liveGroups, setLiveGroups] = useState<{ id: number; members: number[] }[]>([])
  const [freshIds, setFreshIds] = useState<Set<number>>(new Set())
  const [cloudBuilding, setCloudBuilding] = useState(false)
  const [searchInput, setSearchInput] = useState(state.q)
  const debouncedSearch = useDebounced(searchInput, 200)
  const searchRef = useRef<HTMLInputElement>(null)

  const isConceptMode = state.mode === 'moisted'
  const isDocsMode = state.mode === 'dokumendid'

  const collections = useQuery({ queryKey: ['collections'], queryFn: getCollections })
  // URL wins, then whatever was last picked, then the default collection.
  const activeCollection = useActiveCollection(state.collectionId, collections.data)
  // Same query key as the list's own, so react-query serves both from one
  // fetch; this copy exists only to build the domain facet.
  const concepts = useConcepts(activeCollection)
  const conceptDomains = useMemo(
    () =>
      [
        ...new Set(
          (concepts.data ?? []).flatMap((c) =>
            (c.domains ?? '').split(',').map((d) => d.trim()).filter(Boolean),
          ),
        ),
      ].sort(compareStrings),
    [concepts.data],
  )

  // Push the debounced input into the URL (the URL stays the source of truth).
  useEffect(() => {
    if (debouncedSearch !== state.q) patch({ q: debouncedSearch })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  // Adopt external URL changes (back/forward, shared link) into the input.
  useEffect(() => {
    setSearchInput((cur) => (cur === state.q ? cur : state.q))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.q])

  // The documents selected in Dokumendid stay selected across the tabs and
  // narrow Terminid and Mõisted to what they contribute. The header chip is
  // where the user sees (and drops) the selection.
  const docTermIds = useDocumentTermIds(state.documentIds)
  // Same key as the documents list, so the chip's title costs no extra fetch
  // once that view has loaded.
  const documents = useQuery({
    queryKey: ['documents', activeCollection],
    queryFn: () => getDocuments(activeCollection),
    enabled: activeCollection !== null && state.documentIds.length > 0,
  })
  const filterTitles = state.documentIds.map(
    (id) => documents.data?.find((d) => d.id === id)?.title ?? `#${id}`,
  )
  const filterLabel =
    state.documentIds.length === 1
      ? filterTitles[0]
      : t('termBase.documentsSelected', { count: state.documentIds.length })
  // The same filter, for the panels: they explain the count, the chip drops it.
  const documentFilter = state.documentIds.length
    ? {
        titles: filterTitles,
        onOpen: () => patch({ mode: 'dokumendid' as const, term: '', termLang: '' }),
        onClear: () => patch({ documentIds: [] }),
      }
    : null

  const filters = useMemo(
    () => ({
      q: state.q,
      lang: state.lang,
      domain: state.domain,
      abbrOnly: state.abbrOnly,
      termIds: docTermIds,
      sort,
    }),
    [state.q, state.lang, state.domain, state.abbrOnly, docTermIds, sort],
  )

  const { terms, all, total, isLoading, error, refetch } = useFilteredTerms(
    activeCollection,
    filters,
  )
  const { data: domains = [] } = useDomains(activeCollection)

  // The URL stores value+lang; resolve it against the unfiltered layer so that
  // changing a filter never silently empties the detail pane.
  const selected = useMemo<Term | null>(() => {
    if (!state.term) return null
    return (
      all.find(
        (t) => t.value === state.term && (!state.termLang || t.lang === state.termLang),
      ) ?? null
    )
  }, [all, state.term, state.termLang])

  const handleSelect = useCallback((t: Term) => selectTerm(t.value, t.lang), [selectTerm])

  const handleBuildEvent = useCallback((ev: ConceptBuildEvent) => {
    const ids = ev.new_concept.members.map((m) => m.id)
    const cid = ev.new_concept.id
    setLiveGroups((cur) => [...cur, { id: cid, members: ids }])
    setFreshIds((cur) => new Set([...cur, ...ids]))
    // The pop is an arrival flash, not a state — let it fade.
    setTimeout(() => {
      setFreshIds((cur) => {
        const next = new Set(cur)
        for (const id of ids) next.delete(id)
        return next
      })
    }, 1800)
  }, [])

  // A different collection is a different cloud; stale run overlays would
  // colour terms that are not even in it.
  useEffect(() => {
    setLiveGroups([])
    setFreshIds(new Set())
  }, [activeCollection])

  /** Jump to a term from a similar-term chip or a concept card. */
  const jumpToTerm = useCallback(
    (lemma: string, lang: string) => {
      patch(
        { mode: state.mode === 'moisted' ? 'terminid' : state.mode, term: lemma, termLang: lang },
        { replace: false },
      )
    },
    [patch, state.mode],
  )

  const openConcept = useCallback(
    (conceptId: number) => patch({ mode: 'moisted', conceptId }, { replace: false }),
    [patch],
  )

  /**
   * From a mention to the document it came from: the Dokumendid view with
   * that document open at that page and the current term picked out. The
   * term stays in the URL so the text view knows what to highlight; the mode
   * toggle clears it, as it does for any mode change.
   */
  const openDocumentAt = useCallback(
    (documentId: number, page: number) =>
      patch(
        { mode: 'dokumendid', documentId, page, term: state.term, termLang: state.termLang },
        { replace: false },
      ),
    [patch, state.term, state.termLang],
  )

  // "/" focuses search, the way every text-heavy tool does it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName
      if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA') {
        e.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center gap-3 border-b border-border px-3 py-2">
        <CollectionPicker
          collections={collections.data ?? []}
          value={activeCollection}
          onChange={(id) =>
            patch({ collectionId: id, term: '', termLang: '', conceptId: null, documentId: null })
          }
        />
        <SharePanel
          collection={collections.data?.find((c) => c.id === activeCollection)}
        />

        <ToggleGroup
          type="single"
          value={state.mode}
          onValueChange={(v) =>
            v && patch({ mode: v as Mode, term: '', termLang: '', page: null })
          }
        >
          {(Object.keys(MODE_LABEL_KEY) as Mode[]).map((m) => (
            <ToggleGroupItem key={m} value={m}>
              {t(`termBase.modes.${MODE_LABEL_KEY[m]}` as const)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>

        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchRef}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={
              isDocsMode
                ? t('termBase.searchDocuments')
                : isConceptMode
                  ? t('termBase.searchConcepts')
                  : t('termBase.searchTerms')
            }
            className="h-8 pl-8 pr-16"
          />
          <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
            {searchInput ? (
              <Button variant="ghost" size="icon-sm" onClick={() => setSearchInput('')}>
                <X />
              </Button>
            ) : (
              <Kbd>/</Kbd>
            )}
          </div>
        </div>

        {state.documentIds.length > 0 && (
          <Tooltip
            label={
              <div className="max-w-xs">
                <p>{t('termBase.documentFilterHint')}</p>
                {state.documentIds.length > 1 && (
                  <ul className="mt-1 list-disc pl-4">
                    {filterTitles.slice(0, 8).map((title, i) => (
                      <li key={i} className="truncate">
                        {title}
                      </li>
                    ))}
                    {filterTitles.length > 8 && (
                      <li>{t('actions.andMore', { count: filterTitles.length - 8 })}</li>
                    )}
                  </ul>
                )}
              </div>
            }
          >
            <div
              className="flex h-8 max-w-xs items-center gap-1 rounded-md border border-primary/40 bg-primary/10 pl-2 pr-0.5 text-xs"
              aria-label={t('termBase.documentFilter')}
            >
              <FileText className="size-3.5 shrink-0 text-primary" />
              <button
                type="button"
                className="min-w-0 truncate font-medium hover:underline"
                onClick={() => patch({ mode: 'dokumendid', term: '', termLang: '' })}
              >
                {filterLabel}
              </button>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => patch({ documentIds: [] })}
                aria-label={t('termBase.clearDocumentFilter')}
              >
                <X />
              </Button>
            </div>
          </Tooltip>
        )}

        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          <Kbd>⌘K</Kbd> {t('termBase.searchEverywhere')}
        </div>
      </div>

      {error && (
        <div
          className={cn(
            'flex items-center gap-2 border-b px-3 py-2 text-xs',
            isBackendStarting(error)
              ? 'border-warning/30 bg-warning/10 text-warning'
              : 'border-danger/30 bg-danger/10 text-danger',
          )}
        >
          {isBackendStarting(error) ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {apiErrorMessage(error)} {t('common.retryingAutomatically')}
            </>
          ) : (
            <>
              <AlertTriangle className="size-4" />
              {t('common.loadFailed', { message: apiErrorMessage(error) })}
              {traceIdOf(error) && <TraceId id={traceIdOf(error)!} className="ml-1" />}
            </>
          )}
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        {isDocsMode ? (
          <DocumentsView
            collection={collections.data?.find((c) => c.id === activeCollection)}
            query={state.q}
            selectedId={state.documentId}
            selectedIds={state.documentIds}
            onSelect={(id) => patch({ documentId: id, page: null })}
            onSelectionChange={(ids) => patch({ documentIds: ids, page: null })}
            focusTerm={state.term}
            focusPage={state.page}
            onShowTerms={() => patch({ mode: 'terminid', term: '', termLang: '' })}
          />
        ) : isConceptMode ? (
          <>
            <CollapsiblePanel
              id="concept-filters"
              title={t('common.filters')}
              activeCount={
                [state.lang, state.missingLang, state.domain].filter(Boolean).length +
                (state.documentIds.length ? 1 : 0)
              }
            >
              <ConceptFiltersPanel
                documentFilter={documentFilter}
                lang={state.lang}
                missingLang={state.missingLang}
                domain={state.domain}
                sort={conceptSort}
                domains={conceptDomains}
                shown={shownConcepts}
                total={concepts.data?.length ?? 0}
                onChange={patch}
                onSortChange={setConceptSort}
                onClear={() => patch({ lang: '', missingLang: '', domain: '' })}
              />
            </CollapsiblePanel>
            <ConceptsView
              collectionId={activeCollection}
              onClose={() => patch({ conceptId: null })}
              query={state.q}
              lang={state.lang}
              missingLang={state.missingLang}
              domain={state.domain}
              termIds={docTermIds}
              sort={conceptSort}
              selectedId={state.conceptId}
              onSelect={(id) => patch({ conceptId: id })}
              onOpenTerm={jumpToTerm}
              onOpenDocument={openDocumentAt}
              onShownChange={setShownConcepts}
            />
          </>
        ) : (
          <>
            <CollapsiblePanel
              id="term-filters"
              title={t('common.filters')}
              activeCount={
                [state.lang, state.domain].filter(Boolean).length +
                (state.abbrOnly ? 1 : 0) +
                (state.documentIds.length ? 1 : 0)
              }
            >
              <FiltersPanel
                documentFilter={documentFilter}
                lang={state.lang}
                domain={state.domain}
                abbrOnly={state.abbrOnly}
                sort={sort}
                domains={domains}
                shown={terms.length}
                total={total}
                onChange={patch}
                onSortChange={setSort}
                onClear={clearFilters}
              />
            </CollapsiblePanel>

            <section className="flex w-[380px] shrink-0 flex-col border-r border-border">
              <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-1.5 text-[11px] text-muted-foreground">
                {t('units.terms', { count: terms.length })}
                {/* Exports exactly this list — the filters and sort above are
                    already applied, so what downloads is what is on screen. */}
                {!!terms.length && activeCollection && (
                  <Tooltip label={t('terms.exportTerms')}>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="ml-auto"
                      disabled={exporting}
                      aria-label={t('terms.exportTerms')}
                      onClick={async () => {
                        setExporting(true)
                        try {
                          await exportCollectionTerms(
                            activeCollection,
                            terms.map((x) => x.term_id),
                          )
                        } catch (e) {
                          toast.error(t('terms.exportFailed'), {
                            description: apiErrorMessage(e),
                          })
                        } finally {
                          setExporting(false)
                        }
                      }}
                    >
                      {exporting ? <Loader2 className="animate-spin" /> : <Download />}
                    </Button>
                  </Tooltip>
                )}
              </div>
              <div className="min-h-0 flex-1">
                <TermList
                  terms={terms}
                  isLoading={isLoading}
                  query={state.q}
                  selected={selected}
                  onSelect={handleSelect}
                />
              </div>
            </section>

            <section className="min-w-0 flex-1">
              {/* No selection: instead of a sentence saying "pick a term", show
                  the filtered list as a frequency-scaled cloud — an overview
                  that is also a picker. Falls back to TermDetail's own empty
                  state when the filter matches nothing. */}
              {selected || !terms.length ? (
                <TermDetail
                  term={selected}
                  layer="terms"
                  onOpenConcept={openConcept}
                  onOpenDocument={openDocumentAt}
                  onClose={() => patch({ term: '', termLang: '' })}
                />
              ) : (
                <div className="flex h-full min-h-0 flex-col">
                  {activeCollection != null && (
                    <ConceptBuildBar
                      collectionId={activeCollection}
                      grouped={all.filter((x) => x.concept_id != null).length}
                      total={all.length}
                      unchecked={
                        collections.data?.find((c) => c.id === activeCollection)
                          ?.unchecked_count ?? 0
                      }
                      canEdit={
                        collections.data?.find((c) => c.id === activeCollection)
                          ?.my_role !== 'viewer'
                      }
                      onEvent={handleBuildEvent}
                      onRunningChange={setCloudBuilding}
                    />
                  )}
                  <div className="min-h-0 flex-1">
                    <TermCloud
                      terms={terms}
                      onSelect={handleSelect}
                      liveGroups={liveGroups}
                      freshIds={freshIds}
                      building={cloudBuilding}
                    />
                  </div>
                </div>
              )}
            </section>
          </>
        )}
      </div>

      <CommandPalette collectionId={activeCollection} onSelect={handleSelect} />
    </div>
  )
}
